/**
 * Counting game — tap each thing once and say the number.
 *
 * `enforceOneToOne` is the whole pedagogy: a second tap on the same object is
 * refused so the count and the pointing stay locked together. "Give me five"
 * is the harder variant — she must stop at the number instead of counting the
 * whole set.
 */

import { z } from 'zod'
import { EmojiSchema, NonEmptyString, SeedSchema, specFor } from '../common'

export const COUNTING_ARRANGEMENTS = ['scattered', 'line', 'grid'] as const
export const COUNTING_PROMPTS = ['how_many', 'give_me_n', 'count_along'] as const

export const CountingGameConfigSchema = z
  .object({
    /** Singular noun, spoken aloud: "apple". */
    objectType: NonEmptyString.max(40),
    objectEmoji: EmojiSchema,
    count: z.number().int().min(1).max(30),
    arrangement: z.enum(COUNTING_ARRANGEMENTS),
    /** Columns when arranged in a grid. */
    gridColumns: z.number().int().min(2).max(10).default(5),
    /** Each object accepts exactly one tap. */
    enforceOneToOne: z.boolean(),
    allowRecount: z.boolean().default(true),
    tapFeedback: z.enum(['number_appears', 'checkmark', 'both']).default('both'),
    promptStyle: z.enum(COUNTING_PROMPTS),
    /** How many to hand over when the prompt is "give me n". */
    targetNumber: z.number().int().min(1).max(30).optional(),
    /** Extra objects of another kind, so "give me n" needs a real stop. */
    distractorCount: z.number().int().min(0).max(20).default(0),
    distractorEmoji: EmojiSchema.optional(),
    scatterSeed: SeedSchema.default(1),
  })
  .superRefine((config, ctx) => {
    if (config.promptStyle === 'give_me_n') {
      if (config.targetNumber === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['targetNumber'],
          message: '"give me n" needs the number to ask for',
        })
      } else if (config.targetNumber > config.count) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['targetNumber'],
          message: `cannot ask for ${config.targetNumber} when only ${config.count} are on screen`,
        })
      }
    }
    if (config.distractorCount > 0 && !config.distractorEmoji) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['distractorEmoji'],
        message: 'distractors need something to look like',
      })
    }
    if (config.distractorEmoji && config.distractorEmoji === config.objectEmoji) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['distractorEmoji'],
        message: 'distractors must look different from the things being counted',
      })
    }
    if (config.arrangement === 'grid' && config.gridColumns > config.count + config.distractorCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['gridColumns'],
        message: 'more columns than objects leaves an empty row',
      })
    }
  })

export type CountingGameConfig = z.infer<typeof CountingGameConfigSchema>

export const CountingGameSpecSchema = specFor('counting_game', CountingGameConfigSchema)
